import React from 'react';
import {
  Box,
  Checkbox,
  Dialog,
  DialogContent,
  Divider,
  FormControlLabel,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { RouteOptions, RouteProfile } from '../types';

const GOLD = '#f5b942';

const PROFILES: Array<{ id: RouteProfile; label: string; hint: string }> = [
  { id: 'driving-traffic', label: 'Driving, live traffic', hint: 'Default. Traffic-aware ETAs and congestion colouring.' },
  { id: 'driving', label: 'Driving', hint: 'Free-flow speeds, no traffic model.' },
  { id: 'walking', label: 'Walking', hint: 'Footpaths and crossings.' },
  { id: 'cycling', label: 'Cycling', hint: 'Bike-friendly roads and paths.' },
];

const EXCLUDES: Array<{ key: keyof RouteOptions['exclude']; label: string; drivingOnly: boolean }> = [
  { key: 'toll', label: 'Avoid tolls', drivingOnly: true },
  { key: 'motorway', label: 'Avoid highways', drivingOnly: true },
  { key: 'ferry', label: 'Avoid ferries', drivingOnly: false },
  { key: 'unpaved', label: 'Avoid unpaved', drivingOnly: true },
];

interface RouteOptionsDialogProps {
  open: boolean;
  options: RouteOptions;
  onClose: () => void;
  onChange: (next: Partial<RouteOptions>) => void;
}

const RouteOptionsDialog: React.FC<RouteOptionsDialogProps> = ({ open, options, onClose, onChange }) => {
  const isDriving = options.profile === 'driving' || options.profile === 'driving-traffic';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1.25 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 800, flex: 1 }}>Route options</Typography>
        <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
      </Box>
      <Divider />
      <DialogContent sx={{ pt: 1.5 }}>
        <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>Profile</Typography>
        {PROFILES.map((p) => {
          const active = options.profile === p.id;
          return (
            <Box
              key={p.id}
              onClick={() => onChange({ profile: p.id })}
              sx={{
                py: 0.5, px: 0.75, borderRadius: 0.75, cursor: 'pointer',
                borderLeft: '2px solid', borderColor: active ? GOLD : 'transparent',
                bgcolor: active ? 'action.selected' : 'transparent',
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <Typography variant="caption" sx={{ display: 'block', fontWeight: active ? 800 : 600 }}>
                {p.label}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.63rem' }}>
                {p.hint}
              </Typography>
            </Box>
          );
        })}

        <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mt: 1.5, mb: 0.25 }}>Avoid</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
          {EXCLUDES.map((ex) => {
            const supported = ex.drivingOnly ? isDriving : true;
            return (
              <FormControlLabel
                key={ex.key}
                disabled={!supported}
                control={(
                  <Checkbox
                    size="small"
                    checked={supported && options.exclude[ex.key]}
                    onChange={(e) => onChange({ exclude: { ...options.exclude, [ex.key]: e.target.checked } })}
                  />
                )}
                label={<Typography variant="caption">{ex.label}</Typography>}
                sx={{ ml: 0, '& .MuiCheckbox-root': { py: 0.25 } }}
              />
            );
          })}
        </Box>
        {!isDriving && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.63rem' }}>
            Toll, highway, and surface filters only apply to driving profiles.
          </Typography>
        )}

        <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mt: 1.5, mb: 0.5 }}>Departure</Typography>
        <TextField
          type="datetime-local"
          size="small"
          fullWidth
          disabled={options.profile !== 'driving-traffic'}
          value={options.departAt || ''}
          onChange={(e) => onChange({ departAt: e.target.value || null })}
          InputLabelProps={{ shrink: true }}
        />
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontSize: '0.63rem' }}>
          {options.profile === 'driving-traffic'
            ? 'Leave empty to route for right now.'
            : 'Departure time needs the live-traffic profile.'}
        </Typography>

        <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mt: 1.5, mb: 0.5 }}>Units</Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          {(['mi', 'km'] as const).map((u) => (
            <Typography
              key={u}
              variant="caption"
              onClick={() => onChange({ units: u })}
              sx={{
                cursor: 'pointer', fontWeight: options.units === u ? 800 : 500,
                color: options.units === u ? GOLD : 'text.secondary',
              }}
            >
              {u === 'mi' ? 'Miles' : 'Kilometres'}
            </Typography>
          ))}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default RouteOptionsDialog;
